/**
 * Post-incident inspection record for a log.
 *
 * The checklist is half fixed and half derived: a standing list of what to look
 * at on any airframe, plus one item per automated finding, so the physical
 * check the rule asked for is a line someone has to sign rather than a
 * suggestion in a panel they scrolled past.
 *
 * ponytail: state lives in localStorage, keyed by log id. That means one
 * browser, one machine, no audit trail — fine for the engineer working the
 * incident, wrong for a shared airworthiness record. Move to a backend field on
 * the log the day a second person needs to see it; the shape below is already
 * what that endpoint would store.
 */

export const STATES = ["unchecked", "ok", "issue", "na"];

export const STATE_LABELS = {
  unchecked: "Not checked",
  ok: "OK",
  issue: "Issue found",
  na: "N/A",
};

/** What gets looked at on any airframe, regardless of what the log said. */
const BASELINE = [
  { id: "af_props", section: "Airframe", label: "Props/blades — chips, delamination, balance" },
  { id: "af_fasteners", section: "Airframe", label: "Arm and boom fasteners at torque, no witness-mark movement" },
  { id: "af_structure", section: "Airframe", label: "Structure — cracks, deformation, impact marks" },
  { id: "af_cg", section: "Airframe", label: "CG within limits with the flown payload" },
  { id: "pr_bearings", section: "Propulsion", label: "Motor bearings — roughness, axial/radial play" },
  { id: "pr_mounts", section: "Propulsion", label: "Motor mounts and prop adapters secure" },
  { id: "pr_esc", section: "Propulsion", label: "ESCs — heat discolouration, solder joints, connector seating" },
  { id: "pw_pack", section: "Power", label: "Battery pack — swelling, cell balance, resting voltage" },
  { id: "pw_connectors", section: "Power", label: "Power connectors — wear, arcing marks, retention" },
  { id: "av_mount", section: "Avionics", label: "Flight controller mount and vibration isolation intact" },
  { id: "av_gps", section: "Avionics", label: "GPS mast, antenna ground plane, separation from power leads" },
  { id: "av_wiring", section: "Avionics", label: "Wiring — chafe, strain relief, connector seating" },
  { id: "av_sd", section: "Avionics", label: "SD card seated and readable; log written cleanly" },
];

const SECTION_ORDER = ["Findings", "Airframe", "Propulsion", "Power", "Avionics"];

/**
 * Baseline items plus one per finding. Finding-derived items carry the rule's
 * physical hint as their label, so the inspector is told what to look for.
 */
export const checklistFor = (findings = []) => [
  ...findings.map((f) => ({
    id: `finding:${f.id}`,
    section: "Findings",
    label: `${f.title} — ${f.hint}`,
    severity: f.severity,
  })),
  ...BASELINE,
];

export const sectionsOf = (items) =>
  SECTION_ORDER.filter((s) => items.some((i) => i.section === s)).map((section) => ({
    section,
    items: items.filter((i) => i.section === section),
  }));

/* ------------------------------------------------------------- persistence -- */

const storageKey = (logId) => `log-inspection:${logId}`;

export const loadInspection = (logId) => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(logId))) || {};
  } catch {
    return {}; // corrupt or unavailable storage — start clean rather than break the page
  }
};

export const saveInspection = (logId, record) => {
  try {
    localStorage.setItem(storageKey(logId), JSON.stringify(record));
  } catch {
    // Private browsing or a full quota. The report export is still the durable
    // artifact, so losing the draft is survivable and not worth a dialog.
  }
};

export const inspectionProgress = (items, record) => {
  const checked = items.filter((i) => record[i.id]?.state && record[i.id].state !== "unchecked").length;
  const issues = items.filter((i) => record[i.id]?.state === "issue").length;
  return { checked, issues, total: items.length };
};

/** Markdown section for the RCA draft. Returns "" when nothing has been checked. */
export const inspectionSection = (items, record, { inspector } = {}) => {
  const touched = items.filter((i) => record[i.id]?.state && record[i.id].state !== "unchecked");
  if (!touched.length) return "";
  const { checked, issues, total } = inspectionProgress(items, record);
  const lines = [
    "## Inspection record",
    "",
    `${checked} of ${total} items checked, ${issues} issue${issues === 1 ? "" : "s"} found.${inspector ? ` Inspector: ${inspector}.` : ""}`,
    "",
  ];
  for (const { section, items: sectionItems } of sectionsOf(touched)) {
    lines.push(`### ${section}`, "");
    for (const item of sectionItems) {
      const entry = record[item.id];
      const mark = entry.state === "ok" ? "x" : " ";
      lines.push(`- [${mark}] **${STATE_LABELS[entry.state]}** — ${item.label}`);
      if (entry.note) lines.push(`      - ${entry.note}`);
    }
    lines.push("");
  }
  return lines.join("\n");
};
